import { lazy, Suspense } from "react";
import { CityStat, CountryStat } from "../../../interface/dashboard.interface";
import DashCard from "./DashCard";
import { fmtCount } from "./format";

// The map and its world data (~100KB) load only when the dashboard is opened
const UserWorldMap = lazy(() => import("./UserWorldMap"));

const flag = (iso2?: string) =>
  iso2 && /^[A-Za-z]{2}$/.test(iso2)
    ? String.fromCodePoint(...iso2.toUpperCase().split("").map((c) => 127397 + c.charCodeAt(0)))
    : "";

const LocationsCard = ({
  countries,
  cities,
  className,
}: {
  countries?: CountryStat[];
  cities?: CityStat[];
  className?: string;
}) => {
  const sorted = [...(countries ?? [])].sort((a, b) => b.users - a.users);
  const total = sorted.reduce((s, c) => s + c.users, 0);
  const top = sorted.slice(0, 8);
  const others = sorted.slice(8).reduce((s, c) => s + c.users, 0);
  const max = top[0]?.users || 1;

  return (
    <DashCard
      title="Where users are"
      subtitle={
        sorted.length
          ? `${sorted.length} ${sorted.length === 1 ? "country" : "countries"}, ${total.toLocaleString()} users`
          : undefined
      }
      empty={!sorted.length}
      emptyNote="Needs users_by_country from the dashboard API (country name and user count)."
      className={className}
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
        <div className="h-[260px] overflow-hidden rounded-xl bg-[#FBFAFD] sm:h-[340px] lg:h-[380px]">
          <Suspense fallback={<div className="h-full w-full animate-pulse bg-hcBg" />}>
            <UserWorldMap countries={sorted} cities={cities} />
          </Suspense>
        </div>

        <div>
          <p className="mb-3 text-xs font-medium text-hcMuted">Top countries</p>
          <ol className="space-y-3">
            {top.map((c, i) => (
              <li key={c.country}>
                <div className="mb-1 flex items-center gap-2 text-sm">
                  <span className="w-4 text-xs text-hcMuted">{i + 1}</span>
                  <span aria-hidden>{flag(c.iso2)}</span>
                  <span className="truncate text-honeConnectBlack">{c.country}</span>
                  <span className="ml-auto font-semibold text-honeConnectBlack">{fmtCount(c.users)}</span>
                  <span className="w-10 text-right text-xs text-hcMuted">
                    {total ? `${((c.users / total) * 100).toFixed(0)}%` : ""}
                  </span>
                </div>
                <div className="ml-6 h-1.5 rounded-full bg-hcBg">
                  <div
                    className="h-1.5 rounded-full bg-honeConnectPurple"
                    style={{ width: `${(c.users / max) * 100}%` }}
                  />
                </div>
              </li>
            ))}
          </ol>
          {others > 0 && (
            <p className="mt-4 text-xs text-hcMuted">
              + {fmtCount(others)} users in {sorted.length - top.length} other countries
            </p>
          )}
        </div>
      </div>
    </DashCard>
  );
};

export default LocationsCard;
